import { clamp } from 'lodash';

import {
    DEFAULT_IMAGE_MODAL_MAX_SCALE,
    DEFAULT_IMAGE_MODAL_MIN_SCALE,
    ImageModalZoomBounds,
} from './imageModalZoomTypes';
import {
    getFocalLengthForZoomIn,
    getNextScaleFromFocalLengthStep,
} from '../../../../canvas/zoom/utils/zoomFocalLengthStepUtils';
import { getScaleDeltaFromWheelDelta } from '../../../../canvas/zoom/zoomScaleUtils';

/**
 * Clamps the zoom scale to the provided bounds.
 */
export const clampZoomScale = (scale: number, { minScale = 1, maxScale = 4 }: ImageModalZoomBounds = {}): number =>
    clamp(scale, minScale, maxScale);

/**
 * Converts a wheel delta into an image modal zoom delta using the canvas multiplicative wheel curve.
 */
export const getImageModalZoomScaleDeltaFromWheelDelta = (
    deltaY: number,
    currentScale: number,
    { minScale = DEFAULT_IMAGE_MODAL_MIN_SCALE, maxScale = DEFAULT_IMAGE_MODAL_MAX_SCALE }: ImageModalZoomBounds = {},
): number => {
    if (!deltaY) return 0;

    if (currentScale >= maxScale && deltaY <= 0) return 0;
    if (currentScale <= minScale && deltaY >= 0) return 0;

    return getScaleDeltaFromWheelDelta(deltaY, currentScale);
};

/**
 * Applies a zoom increment and clamps the result to the provided bounds.
 */
export const getNextZoomScale = ({
    currentScale,
    scaleDelta,
    minScale = 1,
    maxScale = 4,
}: {
    currentScale: number;
    scaleDelta: number;
} & Required<ImageModalZoomBounds>): number => clampZoomScale(currentScale + scaleDelta, { minScale, maxScale });

/**
 * Calculates the next keyboard zoom scale by stepping along a focal-length curve.
 */
export const getNextImageModalZoomScale = ({
    currentScale,
    direction,
    minScale = 1,
    maxScale = 4,
}: {
    currentScale: number;
    direction: 1 | -1;
} & ImageModalZoomBounds): number =>
    getNextScaleFromFocalLengthStep({
        currentScale,
        focalLength: getFocalLengthForZoomIn(maxScale),
        stepChange: direction,
        minScale,
        maxScale,
    });
